import { z } from 'zod';

const ipv4 =
  /^((25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(25[0-5]|2[0-4]\d|[01]?\d?\d)$/;

const hostname = /^[A-Za-z0-9][A-Za-z0-9.-]{0,253}$/;

const trimmed = (label: string, min = 1, max = 100) =>
  z
    .string()
    .trim()
    .min(min, `${label} is required`)
    .max(max, `${label} must be at most ${max} characters`);

const dialerHost = z
  .string()
  .trim()
  .min(1, 'IP / host is required')
  .refine((v) => ipv4.test(v) || hostname.test(v), {
    message: 'Enter a valid IPv4 address or hostname',
  });

const port = z
  .string()
  .trim()
  .min(1, 'Port is required')
  .refine(
    (v) => /^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 65535,
    { message: 'Port must be a number between 1 and 65535' },
  );

const optionalNonNegativeInt = z
  .string()
  .trim()
  .optional()
  .refine((v) => !v || (/^\d+$/.test(v) && Number(v) >= 0), {
    message: 'Must be a non-negative whole number',
  });

const requiredSelect = (label: string) =>
  z.string().trim().min(1, `Pick a ${label}`);

/* Create — groups picked via multi-select. */
export const dialerCreateSchema = z.object({
  dialerName: trimmed('Dialer name'),
  dialerIP: dialerHost,
  dialerPort: port,
  trunkId: requiredSelect('trunk'),
  locationId: requiredSelect('location'),
  groupIds: z.array(z.string()).min(1, 'Pick at least one group'),
});
export type DialerCreateFormInput = z.infer<typeof dialerCreateSchema>;

/* Update — groups are mutated inline (Add / Remove), so no min(1) here. */
export const dialerUpdateSchema = z.object({
  dialerName: trimmed('Dialer name'),
  dialerIP: dialerHost,
  dialerPort: port,
  trunkId: requiredSelect('trunk'),
  locationId: requiredSelect('location'),
  channelReduction: optionalNonNegativeInt,
  cpsReduction: optionalNonNegativeInt,
});
export type DialerUpdateFormInput = z.infer<typeof dialerUpdateSchema>;

/* Usage filter. */
export const dialerUsageFilterSchema = z
  .object({
    startDate: z.string().min(1, 'Pick a start date'),
    endDate: z.string().min(1, 'Pick an end date'),
  })
  .refine((v) => new Date(v.startDate) <= new Date(v.endDate), {
    message: 'Start date must be on or before end date',
    path: ['endDate'],
  });
export type DialerUsageFilterInput = z.infer<typeof dialerUsageFilterSchema>;
